import axios, {
  type AxiosRequestConfig,
  type AxiosError,
} from "axios"

import Cookies from "js-cookie"

import type { 
    PMPPModel, 
    PMPPPredictionJob, 
    POrder
} from "../types/Types"

const BASE_PMPP_URL = `${import.meta.env.VITE_PMPP_BASE_URL}/${import.meta.env.VITE_PMPP_VER}`
const PMPP_TABPFN_MODELS_FOLDER = import.meta.env.VITE_PMPP_TABPFN_MODELS_FOLDER


// Client

const apiClient = axios.create({
    baseURL: '/',
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true
})


//Interceptors 

let isRefreshing = false
let pendingRequests: (() => void)[] = []

const runPending = () => {
    pendingRequests.forEach((cb) => cb())
    pendingRequests = [];
}

apiClient.interceptors.request.use(
    (config) => {
        const accessToken = Cookies.get("hodor-token");
        if (accessToken && config.headers) {
            config.headers.Authorization = `Bearer ${accessToken}`
        }
        return config
    },
    (error) => Promise.reject(error)
)

apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & {
            _retry?: boolean
        }

        const status = error.response?.status;

        if (status !== 401 || originalRequest._retry) {
            return Promise.reject(error)
        }

        originalRequest._retry = true

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                pendingRequests.push(() => {
                    apiClient(originalRequest).then(resolve).catch(reject)
                })
            })
        }

        isRefreshing = true

        try {
            const refreshToken = Cookies.get("hodor-refresh")
            if (!refreshToken) {
                throw new Error("No refresh token")
            }

            await apiClient.post(
                `/api/v1/refresh-token?refresh_token=${refreshToken}`,
                {},
            )

            runPending()

            return apiClient(originalRequest)

        } catch (refreshError) {
            pendingRequests = []
            // window.location.href = "/login"
            return Promise.reject(refreshError)
        } 
        finally {
            isRefreshing = false
        }
    }
)


const getRequest = async (url:string, params = {}) => {
    try {
        const response = await apiClient.get(url, { params })
        return response.data
    } catch (error:any) {
        // errorCodeHandle(error)
        throw error.response || error
    }
}

const postRequest = async (url:string, data = {}, config = {}) => {
    try {
        const response = await apiClient.post(url, data, config)
        return response.data;
    } catch (error:any) {
        // errorCodeHandle(error)
        throw error.response || error
    }
}

const deleteRequest = async (url:string, params = {}) => {
    try {
        const response = await apiClient.delete(url, {params})
        return response.data
    } catch (error:any) {
        // errorCodeHandle(error)
        throw error.response || error
    }
}
    
//Auth

export const googleAuthRequest = async (body:any) => {
        
    const endpoint = '/api/v1/login/google-one-tap'
    try {
        const res =  await axios.create({
            headers: {
                'Content-type': 'application/x-www-form-urlencoded',
                'X-Requested-With': 'XMLHttpRequest',
                'X-Google-OAuth2-Type': 'client'
            }
        }).post(endpoint, {"credential": body})
        
        return res.data
    }
    catch(e) {
        throw e
    }
}

export const refreshAuthentication = async () => {

    try {
        const refreshToken = Cookies.get("hodor-refresh")
        if (!refreshToken) {
            throw new Error("No refresh token")
        }

        const response = await postRequest(
            `/api/v1/refresh-token?refresh_token=${refreshToken}`
        )
        return response.data
    } catch (error:any) {
        throw error.response || error
    }
}


//Models

export const getModels = async (): Promise<PMPPModel[]> => {
    const endpoint = `${BASE_PMPP_URL}/tabpfn_models/`
    const qs = {
        folder: PMPP_TABPFN_MODELS_FOLDER
    }
    return await getRequest(endpoint, qs)
}


//Prediction Jobs 

export const getPredictionJobs = async (
    page: number, 
    pageSize: number, 
    orderBy?: string, 
    order?: POrder,
):Promise<{
    items: PMPPPredictionJob[],
    page: number, 
    pages: number,
    size: number,
    total: number
}> => {

    let qs:{[key:string]: number | string} = {
        page: page,
        size: pageSize,
    }
        
    if(orderBy && order) {
        qs['order_by'] = orderBy
        qs['order_direction'] = order 
    }

    const endpoint = `${BASE_PMPP_URL}/prediction_jobs/`
    return await getRequest(endpoint, qs)
}


export const getPredictionJob = async (
    id: number
):Promise<PMPPPredictionJob> => {
    const endpoint = `${BASE_PMPP_URL}/prediction_jobs/${id}`
    return await getRequest(endpoint)
}


export const createPredictionJob = async (
    file: File,
    name: string,
    models_names: string[],
    smiles_column: string,
):Promise<PMPPPredictionJob> => {
    
    const endpoint = `${BASE_PMPP_URL}/prediction_jobs/create`

    const formData = new FormData()
    formData.append("name", name)
    formData.append("models_names", models_names.join(','))
    formData.append("models_folder", PMPP_TABPFN_MODELS_FOLDER)
    formData.append("smiles_column", smiles_column)
    formData.append("file", file)

    return await postRequest(
        endpoint, 
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }
    )
}

export const deletePredictionJob = async (id: number) => {
    const endpoint = `${BASE_PMPP_URL}/prediction_jobs/${id}`

    return await deleteRequest(endpoint)
}

export const downloadPredictionJobResults = async (id: number, format: 'csv' | 'sdf') => {
    const endpoint = `${BASE_PMPP_URL}/prediction_jobs/${id}/download_results?format=${format}`
    return await postRequest(endpoint)
}